import React, { useState, useEffect } from "react";
import { Grid, Icon, Table, Segment, Header, Statistic, Message, Button } from "semantic-ui-react";
import "./Dashboard.css";
import DijonCard from "./DijonCard";
import axiosInstance from "../../lib/auth/axiosApi";
import MustardBottleLoader from "../../components/MustardBottleLoader/MustardBottleLoader";

function DijonSchwab(props) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalValue, setTotalValue] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      setError(null);
      setRefreshing(true);
      
      // Fetch accounts
      const accountsRes = await axiosInstance.get("/snaptrade/accounts/");
      const accountsData = accountsRes.data.accounts || [];
      
      // Fetch balances and positions for each account
      const accountsWithDetails = await Promise.all(
        accountsData.map(async (account) => {
          try {
            const [balancesRes, positionsRes] = await Promise.all([
              axiosInstance.get(`/snaptrade/accounts/${account.id}/balances/`),
              axiosInstance.get(`/snaptrade/accounts/${account.id}/positions/`)
            ]);
            
            return {
              ...account,
              balances: balancesRes.data,
              positions: positionsRes.data.positions || []
            };
          } catch (err) {
            console.error(`Error fetching details for account ${account.id}:`, err);
            return {
              ...account,
              balances: {},
              positions: []
            };
          }
        })
      );
      
      setAccounts(accountsWithDetails);
      
      // Calculate total value across all accounts
      const total = accountsWithDetails.reduce((sum, account) => {
        return sum + (account.balances.total_value || 0);
      }, 0);
      setTotalValue(total);
      
    } catch (err) {
      console.error("Error fetching Schwab data:", err);
      setError("Failed to load Schwab account data. Please try again later.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return "0.00%";
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <div id="schwab">
      <DijonCard
        width={'800px'}
        height={'600px'}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={'schwab'}
        content={
          <div className={"container mx-auto px-4"} style={{
            height: '100%',
            overflow: 'auto',
            padding: '20px'
          }}>
            <Header as="h2" icon textAlign="center">
              <Icon name="dollar" circular />
              <Header.Content>Schwab Accounts</Header.Content>
            </Header>

            {loading && !refreshing && <MustardBottleLoader />}
            
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}

            {!loading && !error && (
              <>
                <Segment textAlign="center" inverted color="green">
                  <Statistic inverted>
                    <Statistic.Value>{formatCurrency(totalValue)}</Statistic.Value>
                    <Statistic.Label>Total Portfolio Value</Statistic.Label>
                  </Statistic>
                </Segment>

                <Button 
                  primary 
                  fluid 
                  onClick={fetchAccountData} 
                  loading={refreshing}
                  disabled={refreshing}
                  style={{ marginBottom: '20px' }}
                >
                  <Icon name="refresh" /> Refresh Data
                </Button>

                {accounts.length === 0 ? (
                  <Message info>
                    <Message.Header>No accounts found</Message.Header>
                    <p>No Schwab accounts are linked to your profile.</p>
                  </Message>
                ) : (
                  accounts.map((account) => (
                    <Segment key={account.id} style={{ marginBottom: '20px' }}>
                      <Header as="h3">
                        {account.name || `Account ${account.id}`}
                        <Header.Subheader>
                          {account.type} - {account.brokerage}
                        </Header.Subheader>
                      </Header>

                      <Grid columns={3} divided>
                        <Grid.Row>
                          <Grid.Column>
                            <Statistic size="tiny">
                              <Statistic.Value>{formatCurrency(account.balances.cash)}</Statistic.Value>
                              <Statistic.Label>Cash</Statistic.Label>
                            </Statistic>
                          </Grid.Column>
                          <Grid.Column>
                            <Statistic size="tiny">
                              <Statistic.Value>{formatCurrency(account.balances.total_value)}</Statistic.Value>
                              <Statistic.Label>Total Value</Statistic.Label>
                            </Statistic>
                          </Grid.Column>
                          <Grid.Column>
                            <Statistic size="tiny">
                              <Statistic.Value>{formatCurrency(account.balances.buying_power)}</Statistic.Value>
                              <Statistic.Label>Buying Power</Statistic.Label>
                            </Statistic>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>

                      {account.positions.length > 0 && (
                        <>
                          <Header as="h4" style={{ marginTop: '20px' }}>Positions</Header>
                          <Table celled striped>
                            <Table.Header>
                              <Table.Row>
                                <Table.HeaderCell>Symbol</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Quantity</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Avg Cost</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Current Price</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Market Value</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Gain/Loss</Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {account.positions.map((position) => {
                                const gainLoss = position.market_value - (position.quantity * position.average_cost);
                                const gainLossPercent = ((position.current_price - position.average_cost) / position.average_cost) * 100;
                                
                                return (
                                  <Table.Row key={position.symbol}>
                                    <Table.Cell>
                                      <strong>{position.symbol}</strong>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">{position.quantity}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(position.average_cost)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(position.current_price)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(position.market_value)}</Table.Cell>
                                    <Table.Cell textAlign="right" positive={gainLoss >= 0} negative={gainLoss < 0}>
                                      {formatCurrency(gainLoss)}
                                      <br />
                                      <small>{formatPercent(gainLossPercent)}</small>
                                    </Table.Cell>
                                  </Table.Row>
                                );
                              })}
                            </Table.Body>
                          </Table>
                        </>
                      )}
                    </Segment>
                  ))
                )}
              </>
            )}
          </div>
        }
      />
    </div>
  );
}

export default DijonSchwab;